import React, { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import * as Icons from 'lucide-react';
import {
  Clock, ShieldAlert, Wrench, ArrowLeft, CheckCircle2, MapPin, Plus,
  Heart, MessageCircle, Mountain, Bike, Footprints, ExternalLink, Trophy, Zap,
  ChevronLeft, ChevronRight,
} from 'lucide-react';
import { useProfile } from '../context/ProfileContext';
import { recoveryActivities } from '../data/endurance';
import { getScheduleForWeek, todayIdx, localISO } from '../data/programs';
import { useTierState } from '../hooks/useTierState';
import { useFeatureFlag } from '../context/FeatureFlagsContext';
import PaywallModal from '../components/PaywallModal';
import GearRecommendations from '../components/GearRecommendations';
import WidgetGate from '../components/WidgetGate';
import GpsTracker from '../components/GpsTracker';
import { savePoints } from '../lib/gpsStore';
import { distanceUnit, displayDistance, parseDistanceInput } from '../utils/units';
import IntervalList from '../components/IntervalList';
import AIEnduranceModal from '../components/AIEnduranceModal';

// Activities where outdoor GPS tracking makes sense in Pass 1.
// walk-run + cycling rely on the outdoor MODE flag, hiking is always outdoor.
const isGpsEligible = (activity, mode) => {
  if (!activity) return false;
  if (activity.id === 'hiking') return true;
  if ((activity.id === 'walk-run' || activity.id === 'cycling') && mode === 'outdoor') return true;
  return false;
};

/**
 * Derive today's default intensity from the user's weekly schedule.
 *
 *   - schedule slot 'recovery' → 'easy'      (rest-day style, low-intensity active recovery)
 *   - schedule slot 'rest'     → 'easy'      (truly rest — still suggest easy if user opens the page)
 *   - schedule slot training   → 'steady'    (normal training day, sustainable pace)
 *
 * 'sport' (race / comp) is never auto-set today — that's an explicit user choice
 * for now, until we wire in a separate race-day flag.
 */
const defaultIntensityFromSchedule = (profile) => {
  const programType = profile.programType || 'PPL';
  let schedule = Array.isArray(profile.schedule) && profile.schedule.length === 7
    ? profile.schedule
    : null;
  if (!schedule) {
    try {
      schedule = getScheduleForWeek({
        programId: programType,
        experience: profile.experience,
        daysPerWeek: profile.daysPerWeek || 4,
        startDate: profile.startDate || localISO(new Date()),
        hasRecovery: (profile.recoveryActivities || []).length > 0,
        viewDate: new Date(),
      });
    } catch {
      return 'steady';
    }
  }
  if (!Array.isArray(schedule) || schedule.length !== 7) return 'steady';
  const idx = todayIdx() % 7;
  const slot = schedule[idx];
  if (slot === 'recovery' || slot === 'rest') return 'easy';
  return 'steady';
};

const Endurance = () => {
  const { profile, history, completeWorkout } = useProfile();
  const navigate = useNavigate();
  const userActs = profile.recoveryActivities || [];
  // If user has activities, filter to those — but fall back to all if the filter ends up empty
  // (defensive against any future ID changes).
  const matchedActs = userActs.length ? recoveryActivities.filter((a) => userActs.includes(a.id)) : [];
  const filtered = matchedActs.length > 0 ? matchedActs : recoveryActivities;

  const [activeId, setActiveId] = useState(filtered[0]?.id || recoveryActivities[0].id);
  const [mode, setMode] = useState('indoor'); // for activities that have indoor/outdoor
  const [surfaceId, setSurfaceId] = useState(null);
  const [selectedIdx, setSelectedIdx] = useState(null); // null = level default; index into the visible workouts array
  // Intensity dial (Endurance v1): user picks the intent of TODAY's session.
  // Easy = active-recovery pace. Steady = sustainable training. Sport = race/comp pace.
  // Default is derived from today's schedule slot (rest/recovery day → easy, training day → steady).
  // Stored in the logged session's meta so analytics can split adherence-by-intent later.
  const [intensity, setIntensity] = useState(() => defaultIntensityFromSchedule(profile));
  const [logOpen, setLogOpen] = useState(false);
  const [logForm, setLogForm] = useState({ platform: 'manual', duration: '', distance: '', notes: '' });
  // Historical logging — date defaults to today; user can pick any date in the
  // last 14 days. We pass `date` to completeWorkout which already honors it.
  const [customLogDate, setCustomLogDate] = useState(localISO(new Date()));
  // Log-time mode/surface override — when the user opens the log dialog, these
  // initialize from current page state but can be changed in the dialog
  // without affecting the page. Lets users backfill an outdoor run while
  // currently viewing indoor-walk, etc.
  const [logMode, setLogMode] = useState(null);       // 'indoor' | 'outdoor' | null (no override)
  const [logSurfaceId, setLogSurfaceId] = useState(null); // override surface for this log only
  // GPS tracker takeover (foreground only — Pass 1)
  const [gpsOpen, setGpsOpen] = useState(false);
  const [gpsPaywallOpen, setGpsPaywallOpen] = useState(false);
  // AI session generator (interval-based surfaces only) — paid gate mirrors GPS.
  const [aiOpen, setAiOpen] = useState(false);
  const [aiPaywallOpen, setAiPaywallOpen] = useState(false);
  const { is_paid: isPaid } = useTierState();
  const { enabled: gpsFeatureEnabled } = useFeatureFlag('gps_endurance');

  const selected = recoveryActivities.find((a) => a.id === activeId) || recoveryActivities[0];
  const hasModes = !!selected.modes;
  const currentMode = hasModes ? selected.modes[mode] : null;
  const userLevel = profile.experience || 'beginner';

  // Determine surfaces visible (based on user's equipment)
  const visibleSurfaces = useMemo(() => {
    if (!currentMode) return [];
    return currentMode.surfaces.filter((s) => {
      if (!s.requiresEquipment || s.requiresEquipment.length === 0) return true;
      if (profile.fullGym) return true;
      return s.requiresEquipment.every((eq) => (profile.equipment || []).includes(eq));
    });
  }, [currentMode, profile.equipment, profile.fullGym]);

  const activeSurface = visibleSurfaces.find((s) => s.id === surfaceId) || visibleSurfaces[0];

  // Workouts to show: either from active surface or flat top-level
  const workouts = hasModes ? (activeSurface?.workouts || []) : (selected.workouts || []);
  // Selection is keyed by the workout's INDEX, not its level — multiple workouts
  // can share a level (e.g. two "advanced" on one surface), so a level key would
  // highlight and resolve all of them at once. Default to the first workout
  // matching the user's experience level, else the first workout. selectedIdx is
  // reset to null whenever the activity/mode/surface (and thus workouts) changes.
  const defaultIdx = Math.max(0, workouts.findIndex((w) => w.level === userLevel));
  const effectiveIdx = (selectedIdx != null && selectedIdx < workouts.length) ? selectedIdx : defaultIdx;
  const recommended = workouts[effectiveIdx];
  const effectiveLevel = recommended?.level;
  const isCustomPick = selectedIdx != null && selectedIdx !== defaultIdx;

  // Guidance tier for this view. 'self' = machine/platform-driven (built-in
  // programs, connected classes); we hide the interval prescription + level
  // cards and show a compact self-guided card with the Log button. Defaults to
  // 'full' (structured intervals / prose). Sourced from the active surface
  // (moded activities) or the activity itself (flat activities).
  const guidance = (hasModes ? activeSurface?.guidance : selected.guidance) || 'full';
  const selfGuidedNote = hasModes ? activeSurface?.selfGuidedNote : selected.selfGuidedNote;

  // Real session history for the selected activity (replaces mock feed)
  const sessionHistory = useMemo(
    // Sort by ACTIVITY DATE (`h.date`) descending, not insertion order. A
    // historical workout logged today for last Tuesday should still appear
    // after today's session in the carousel.
    () => (history || [])
      .filter((h) => h.meta?.recoveryId === selected.id)
      .slice()
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()),
    [history, selected.id]
  );

  const handleSelectActivity = (id) => {
    setActiveId(id);
    setSurfaceId(null);
    setMode('indoor');
    setSelectedIdx(null);
  };

  // GPS tracker → save raw points + push a fully-fledged completeWorkout entry
  // with the GPS stats baked into meta.gps. Activity records that come from
  // GPS are flagged platform='gps' so the history feed and ActivityDetail can
  // route the route-preview UI off it.
  const handleGpsComplete = async ({ record, rawPoints, startedAt, endedAt }) => {
    const clientId = `wk-${Date.now()}`;
    const surfaceName = activeSurface?.name;
    const modeLabel = hasModes ? (mode === 'outdoor' ? 'Outdoor' : 'Indoor') : '';
    const titleSuffix = surfaceName ? ` · ${surfaceName}` : modeLabel ? ` · ${modeLabel}` : '';
    const units = profile.units || 'imperial';
    // session.distance is stored in CANONICAL MILES regardless of user pref;
    // display layer converts. meta.gps.stats.distanceM holds the meters truth.
    const distanceMi = +(record.stats.distanceM / 1609.344).toFixed(3);
    const durationMin = Math.max(1, Math.round(record.stats.durationS / 60));

    try {
      await savePoints(clientId, rawPoints);
    } catch (err) {
      // Non-fatal — record still saves without raw points; polyline + splits live in meta.gps
      // eslint-disable-next-line no-console
      console.warn('[gps] savePoints failed; continuing with derived-only record', err);
    }

    completeWorkout({
      id: clientId,
      title: `${selected.name}${titleSuffix}`,
      date: new Date(endedAt).toISOString(),
      // TZ stamp captured at log time — see pacing.py for usage.
      tz_offset_minutes: new Date().getTimezoneOffset(),
      duration: durationMin,
      distance: distanceMi,
      exerciseCount: 1,
      exercisesCompleted: 1,
      totalVolume: 0,
      muscles: ['Cardio'],
      meta: {
        platform: 'gps',
        recoveryId: selected.id,
        mode: hasModes ? mode : 'standard',
        surface: surfaceName || null,
        intensity,
        gps: record,
        units,
      },
      exercises: [],
    });
    setGpsOpen(false);
    toast.success('Activity logged', {
      description: 'See it on the Progress page',
      action: { label: 'View', onClick: () => navigate('/progress') },
    });
  };

  // Effective mode/surface for the open log dialog. Falls back to page state
  // when no override is set.
  const effectiveLogMode = logMode ?? mode;
  const effectiveLogSurfaces = useMemo(() => {
    if (!hasModes) return [];
    return (selected.modes[effectiveLogMode]?.surfaces || []).filter((s) => {
      if (!s.requiresEquipment || s.requiresEquipment.length === 0) return true;
      if (profile.fullGym) return true;
      return s.requiresEquipment.every((eq) => (profile.equipment || []).includes(eq));
    });
  }, [hasModes, selected, effectiveLogMode, profile.equipment, profile.fullGym]);
  const effectiveLogSurface = effectiveLogSurfaces.find((s) => s.id === logSurfaceId)
    || effectiveLogSurfaces[0]
    || null;

  // Open the log dialog with current page state as the default override
  const openLogDialog = () => {
    setLogMode(hasModes ? mode : null);
    setLogSurfaceId(activeSurface?.id || null);
    setLogOpen(true);
  };

  const handleLogSession = () => {
    // Use the log-dialog overrides (which default to page state).
    const dialogMode = hasModes ? effectiveLogMode : null;
    const dialogSurface = effectiveLogSurface;
    const surfaceName = dialogSurface?.name;
    const modeLabel = hasModes ? (dialogMode === 'outdoor' ? 'Outdoor' : 'Indoor') : '';
    const titleSuffix = surfaceName ? ` · ${surfaceName}` : modeLabel ? ` · ${modeLabel}` : '';
    // Build the date payload. customLogDate is a YYYY-MM-DD string; convert to
    // local end-of-day so calendar grouping lands cleanly. If the user kept
    // today, default to "now" so the timestamp reflects when they actually
    // logged it.
    const todayISO = localISO(new Date());
    let datePayload;
    if (customLogDate && customLogDate !== todayISO) {
      const [y, m, d] = customLogDate.split('-').map(Number);
      const logDate = new Date(y, m - 1, d, 23, 59, 0, 0);
      datePayload = logDate.toISOString();
    }
    completeWorkout({
      title: `${selected.name}${titleSuffix}`,
      ...(datePayload ? { date: datePayload } : {}),
      // TZ stamp captured at log time — see pacing.py for usage.
      tz_offset_minutes: new Date().getTimezoneOffset(),
      duration: Number(logForm.duration) || recommended?.duration || 30,
      // Distance is stored in canonical miles regardless of user pref so the
      // display layer can always convert based on the current units setting.
      distance: logForm.distance ? +parseDistanceInput(logForm.distance, profile.units).toFixed(3) : undefined,
      exerciseCount: 1,
      exercisesCompleted: 1,
      totalVolume: 0,
      muscles: ['Cardio'],
      meta: {
        platform: logForm.platform,
        distance: logForm.distance,
        notes: logForm.notes,
        recoveryId: selected.id,
        mode: dialogMode || 'standard',
        surface: surfaceName || null,
        surfaceId: dialogSurface?.id || null,
        intensity,
      },
      exercises: [],
    });
    setLogOpen(false);
    setLogForm({ platform: 'manual', duration: '', distance: '', notes: '' });
    setCustomLogDate(localISO(new Date()));
    setLogMode(null);
    setLogSurfaceId(null);
    toast.success('Activity logged', {
      description: 'See it on the Progress page',
      action: { label: 'View', onClick: () => navigate('/progress') },
    });
  };

  const openAiOrPaywall = () => {
    if (isPaid) setAiOpen(true);
    else setAiPaywallOpen(true);
  };

  // Log an AI-generated endurance session. Duration is derived from the summed
  // interval time (expanding repeat blocks) so Progress totals stay honest even
  // though the user never touched the manual log form.
  const handleApplyAiSession = (session) => {
    const surfaceName = activeSurface?.name;
    const modeLabel = hasModes ? (mode === 'outdoor' ? 'Outdoor' : 'Indoor') : '';
    const titleSuffix = surfaceName ? ` · ${surfaceName}` : modeLabel ? ` · ${modeLabel}` : '';
    const sumDuration = (ivals) => (ivals || []).reduce((acc, iv) => {
      if (iv && Array.isArray(iv.steps)) return acc + (Number(iv.repeat) || 1) * sumDuration(iv.steps);
      return acc + (Number(iv?.durationMin) || 0) + (Number(iv?.durationSec) || 0) / 60;
    }, 0);
    const dur = Math.round(sumDuration(session.intervals)) || recommended?.duration || 30;
    completeWorkout({
      title: `${session.title || selected.name}${titleSuffix}`,
      tz_offset_minutes: new Date().getTimezoneOffset(),
      duration: dur,
      exerciseCount: 1,
      exercisesCompleted: 1,
      totalVolume: 0,
      muscles: ['Cardio'],
      meta: {
        platform: 'ai',
        recoveryId: selected.id,
        mode: hasModes ? mode : 'standard',
        surface: surfaceName || null,
        surfaceId: activeSurface?.id || null,
        intensity,
        ai: true,
        aiSession: { title: session.title, focus: session.focus, intervals: session.intervals },
      },
      exercises: [],
    });
    toast.success('AI session logged', {
      description: 'See it on the Progress page',
      action: { label: 'View', onClick: () => navigate('/progress') },
    });
  };

  return (
    <div className="bg-transparent text-enf-text-primary min-h-screen pt-20 pb-16">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
          <div>
            <div className="text-enf-role-info text-xs uppercase tracking-[0.3em] font-semibold mb-2">Endurance</div>
            <h1 className="font-display text-6xl md:text-8xl tracking-wide leading-none">Go the distance.</h1>
            <p className="text-enf-text-secondary mt-2 max-w-xl">Run, ride, climb, swim, row, walk. Pick your activity, dial in your intent — easy active-recovery, steady training, or full sport pace. Indoor or outdoor, we tailor the session to today.</p>
          </div>
        </div>

        <div className="grid lg:grid-cols-4 gap-5">
          {/* Activity selector */}
          <div className="lg:col-span-1 space-y-2">
            <div className="text-xs uppercase tracking-widest text-enf-text-muted font-semibold mb-3 px-1">
              {matchedActs.length > 0 ? 'Your activities' : 'All activities'}
            </div>
            {filtered.map((a) => {
              const Ico = Icons[a.icon] || Icons.Activity;
              return (
                <button key={a.id} onClick={() => handleSelectActivity(a.id)}
                  className={`w-full text-left p-3 rounded-md border flex items-center gap-3 transition-colors ${
                    activeId === a.id ? 'bg-enf-accent/10 border-enf-accent-fg' : 'bg-enf-surface-deep border-enf-surface-border hover:border-enf-surface-border'
                  }`}>
                  <Ico className={`w-4 h-4 ${activeId === a.id ? 'text-enf-accent-fg' : 'text-enf-text-muted'} ${a.icon === 'Flame' ? 'scale-x-[-1]' : ''}`} />
                  <span className="text-sm font-medium">{a.name}</span>
                </button>
              );
            })}
            {!matchedActs.length && userActs.length > 0 && (
              <button onClick={() => navigate('/onboarding')} className="w-full mt-3 text-xs text-enf-role-warning hover:text-enf-text-primary underline underline-offset-4">
                Your saved activities don't match — refresh your profile →
              </button>
            )}
            {!userActs.length && (
              <button onClick={() => navigate('/onboarding')} className="w-full mt-3 text-xs text-enf-text-muted hover:text-enf-role-info underline underline-offset-4">
                Set your preferred activities →
              </button>
            )}
          </div>

          {/* Right panel */}
          <div className="lg:col-span-3 space-y-5 min-w-0">
            <div className="relative aspect-[3/1] rounded-xl overflow-hidden bg-enf-surface-deep border border-enf-surface-border">
              <img
                src={(hasModes && currentMode?.image) || selected.image}
                alt={selected.name}
                className="absolute inset-0 w-full h-full object-cover opacity-70"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
              <div className="absolute bottom-0 left-0 p-6">
                <div className="text-xs uppercase tracking-[0.3em] text-enf-role-info font-semibold mb-1">Endurance</div>
                <h2 className="font-display text-5xl md:text-6xl tracking-wide leading-none">{selected.name}</h2>
              </div>
            </div>

            {/* Intensity dial — today's intent for this session.
                Easy = active-recovery pace. Steady = sustainable training. Sport = race / comp.
                State-only for now; the chosen intensity is stamped on logged sessions for later analytics. */}
            <div
              className="p-4 bg-enf-surface-deep border border-enf-surface-border rounded-lg"
              data-testid="intensity-dial"
            >
              <div className="text-[10px] uppercase tracking-[0.3em] text-enf-text-muted font-semibold mb-3">Today's intensity</div>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { id: 'easy', label: 'Easy', sub: 'Active recovery pace', accent: 'border-adaptive-sage/60 bg-adaptive-sage/10 text-adaptive-sage' },
                  { id: 'steady', label: 'Steady', sub: 'Sustainable training pace', accent: 'border-enf-role-info/60 bg-enf-role-info/10 text-enf-role-info' },
                  { id: 'sport', label: 'Sport', sub: 'Race / competition pace', accent: 'border-enf-accent-fg bg-enf-accent/15 text-enf-accent-fg' },
                ].map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setIntensity(opt.id)}
                    className={`p-3 rounded-md border text-left transition-colors ${
                      intensity === opt.id
                        ? opt.accent
                        : 'bg-enf-surface-mid border-enf-surface-border text-enf-text-secondary hover:border-enf-surface-border hover:text-enf-text-primary'
                    }`}
                    data-testid={`intensity-${opt.id}`}
                  >
                    <div className="text-sm font-semibold">{opt.label}</div>
                    <div className="text-[11px] mt-0.5 opacity-80">{opt.sub}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Indoor/Outdoor toggle */}
            {hasModes && (
              <div className="flex items-center gap-2 p-2 bg-enf-surface-deep border border-enf-surface-border rounded-lg w-fit">
                {['indoor', 'outdoor'].map((m) => (
                  <button key={m} onClick={() => { setMode(m); setSurfaceId(null); setSelectedIdx(null); }}
                    className={`px-5 py-2 rounded-md text-sm font-semibold uppercase tracking-wider transition-colors ${
                      mode === m ? 'bg-enf-accent text-enf-text-primary' : 'text-enf-text-secondary hover:text-enf-text-primary'
                    }`}>
                    {selected.modes[m].label}
                  </button>
                ))}
              </div>
            )}

            {/* Surface chips (when applicable) */}
            {hasModes && visibleSurfaces.length > 1 && (
              <div className="flex flex-wrap gap-2">
                {visibleSurfaces.map((s) => (
                  <button key={s.id} onClick={() => { setSurfaceId(s.id); setSelectedIdx(null); }}
                    className={`px-3.5 py-1.5 text-sm rounded-full border transition-colors ${
                      activeSurface?.id === s.id ? 'bg-enf-surface-special border-enf-border-special text-enf-text-primary' : 'bg-enf-surface-deep border-enf-surface-border text-enf-text-secondary hover:border-enf-surface-border'
                    }`}>
                    {s.name}
                  </button>
                ))}
              </div>
            )}
            {hasModes && visibleSurfaces.length === 0 && (
              <div className="p-4 bg-enf-surface-deep border border-enf-role-warning/30 rounded-md text-sm text-enf-text-secondary">
                <div className="text-enf-role-warning font-semibold mb-1">No matching equipment</div>
                <span className="text-enf-text-secondary">Add the required equipment in your profile to unlock these workouts.</span>{' '}
                <Link to="/profile" className="text-enf-role-info underline underline-offset-4">Update profile</Link>
              </div>
            )}

            {/* Selected workout — adaptive badge, always shows log button */}
            {guidance !== 'self' && recommended && (
              <div className="bg-enf-surface-deep border border-enf-surface-border rounded-xl p-6">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className={`w-4 h-4 ${isCustomPick ? 'text-enf-role-info' : 'text-enf-accent-fg'}`} />
                    <span className="text-xs uppercase tracking-widest text-enf-text-secondary font-semibold">
                      {isCustomPick
                        ? <>Your pick <span className="text-enf-role-info capitalize">({effectiveLevel})</span> · Set up for <span className="text-enf-text-secondary capitalize">{userLevel}</span></>
                        : <>Recommended for you <span className="text-enf-accent-fg capitalize">({userLevel})</span></>}
                    </span>
                  </div>
                  <div className="flex items-center gap-1 text-sm text-enf-text-secondary"><Clock className="w-4 h-4" /> {recommended.duration} min</div>
                </div>
                <div className="font-display text-3xl tracking-wide leading-none mb-2">{recommended.name}</div>
                {recommended.intervals
                  ? <IntervalList intervals={recommended.intervals} units={profile.units} />
                  : <p className="text-enf-text-secondary">{recommended.detail}</p>}
                <div className="mt-4 flex flex-wrap gap-2">
                  <button onClick={openLogDialog} className="inline-flex items-center gap-2 px-4 py-2 bg-enf-accent hover:bg-enf-accent-hover rounded-md text-sm font-semibold transition-colors" data-testid="recovery-log-session">
                    <Plus className="w-4 h-4" /> Log this session
                  </button>
                  {recommended.intervals && (
                    <button
                      onClick={openAiOrPaywall}
                      className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold transition-colors ${
                        isPaid
                          ? 'bg-enf-role-info/10 hover:bg-enf-role-info/20 border border-enf-role-info/40 text-enf-role-info'
                          : 'bg-enf-accent/10 hover:bg-enf-accent/20 border border-enf-accent-fg/40 text-enf-accent-fg'
                      }`}
                      data-testid="recovery-ai-session"
                      title={isPaid ? 'Generate a personalized AI session for today' : 'AI sessions are an Enferno feature'}
                    >
                      {isPaid ? <Icons.Sparkles className="w-4 h-4" /> : <Icons.Lock className="w-4 h-4" />}
                      {isPaid ? 'Generate AI session' : 'AI session — unlock Enferno'}
                    </button>
                  )}
                  {isGpsEligible(selected, mode) && gpsFeatureEnabled && (
                    <button
                      onClick={() => { if (isPaid) setGpsOpen(true); else setGpsPaywallOpen(true); }}
                      className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold transition-colors ${
                        isPaid
                          ? 'bg-enf-role-info/10 hover:bg-enf-role-info/20 border border-enf-role-info/40 text-enf-role-info'
                          : 'bg-enf-accent/10 hover:bg-enf-accent/20 border border-enf-accent-fg/40 text-enf-accent-fg'
                      }`}
                      data-testid="recovery-track-gps"
                      title={isPaid ? 'Live GPS tracking — distance, pace, splits, route preview' : 'GPS tracking is an Enferno feature'}
                    >
                      {isPaid ? <MapPin className="w-4 h-4" /> : <Icons.Lock className="w-4 h-4" />}
                      {isPaid ? 'Track outdoors with GPS' : 'GPS — unlock Enferno'}
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Self-guided activities (machine/platform-driven): one compact card
                that defers to built-in programs and keeps the Log button. Replaces
                the recommended card + level grid above — net less vertical space. */}
            {guidance === 'self' && (
              <div className="bg-enf-surface-deep border border-enf-surface-border rounded-xl p-6">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle2 className="w-4 h-4 text-enf-accent-fg" />
                  <span className="text-xs uppercase tracking-widest text-enf-text-secondary font-semibold">Self-guided</span>
                </div>
                <p className="text-enf-text-secondary mb-4 leading-relaxed">
                  {selfGuidedNote || 'This activity is guided by your equipment’s built-in programs. Pick one that matches today’s effort, then log it here.'}
                </p>
                <button onClick={openLogDialog} className="inline-flex items-center gap-2 px-4 py-2 bg-enf-accent hover:bg-enf-accent-hover rounded-md text-sm font-semibold transition-colors" data-testid="recovery-log-session-selfguided">
                  <Plus className="w-4 h-4" /> Log this session
                </button>
              </div>
            )}

            {/* All level options — all clickable */}
            {guidance !== 'self' && workouts.length > 0 && (
              <div className="grid md:grid-cols-3 gap-3">
                {workouts.map((w, i) => {
                  const isActive = i === effectiveIdx;
                  return (
                    <button
                      key={i}
                      onClick={() => setSelectedIdx(i)}
                      className={`text-left p-4 rounded-md border transition-colors ${
                        isActive
                          ? 'bg-enf-accent/10 border-enf-accent-fg'
                          : 'bg-enf-surface-deep border-enf-surface-border hover:border-enf-surface-border'
                      }`}
                      data-testid={`level-card-${w.level}-${i}`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <div className={`text-xs uppercase tracking-widest font-semibold capitalize ${isActive ? 'text-enf-accent-fg' : 'text-enf-text-muted'}`}>{w.level}</div>
                        <div className="text-xs text-enf-text-muted font-mono">{w.duration}m</div>
                      </div>
                      <div className="font-bold mb-1">{w.name}</div>
                      {w.intervals
                        ? <IntervalList intervals={w.intervals} units={profile.units} compact />
                        : <div className="text-xs text-enf-text-secondary leading-relaxed">{w.detail}</div>}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Connected platforms (indoor cycling only) */}
            {hasModes && mode === 'indoor' && currentMode.connectedPlatforms && (
              <div>
                <div className="text-xs uppercase tracking-widest text-enf-role-info font-semibold mb-3">Connect your platform</div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  {currentMode.connectedPlatforms.map((p) => (
                    <div key={p.id} className="relative bg-enf-surface-deep border border-enf-surface-border hover:border-enf-surface-border rounded-lg p-4 transition-colors">
                      <div className="absolute top-2 right-2 text-[10px] uppercase tracking-widest text-enf-role-warning bg-enf-role-warning/10 px-1.5 py-0.5 rounded">Coming soon</div>
                      <div className="font-display text-2xl tracking-wide leading-none">{p.name}</div>
                      <div className="text-xs text-enf-text-secondary mt-1">{p.tag}</div>
                      <button disabled className="mt-3 w-full py-1.5 bg-enf-surface-mid text-enf-text-muted rounded text-xs font-semibold cursor-not-allowed flex items-center justify-center gap-1">
                        <ExternalLink className="w-3 h-3" /> Connect
                      </button>
                    </div>
                  ))}
                </div>
                <button onClick={openLogDialog} className="mt-3 w-full p-4 bg-enf-surface-special/40 hover:bg-enf-surface-special/70 border border-enf-border-special/40 hover:border-enf-border-special rounded-lg text-left transition-colors flex items-center justify-between">
                  <div>
                    <div className="font-display text-xl tracking-wide leading-none">Or log a session manually</div>
                    <div className="text-xs text-enf-text-secondary mt-1">Track Peloton, Zwift, or any indoor ride right now.</div>
                  </div>
                  <Plus className="w-5 h-5 text-enf-accent-fg" />
                </button>
              </div>
            )}

            {/* Recent sessions carousel — real history filtered by this activity */}
            {sessionHistory.length > 0 && (
              <WidgetGate page="recovery" id="recentSessions">
                <div>
                <div className="flex items-center justify-between mb-3">
                  <div className="text-xs uppercase tracking-widest text-enf-role-info font-semibold">Your recent {selected.name.toLowerCase()} sessions</div>
                  <span className="text-xs text-enf-text-muted">{sessionHistory.length} logged · swipe →</span>
                </div>
                <div className="flex gap-3 overflow-x-auto snap-x snap-mandatory pb-3 scrollbar-thin" data-testid="recent-sessions-carousel">
                  {sessionHistory.map((h) => {
                    const Ico = selected.id === 'cycling' ? Bike : selected.id === 'walk-run' ? Footprints : (selected.id === 'hiking' || selected.id === 'rock-climbing') ? Mountain : Footprints;
                    const dt = new Date(h.date);
                    return (
                      <Link
                        key={h.id}
                        to={`/activity/${h.id}`}
                        className="snap-start flex-shrink-0 w-[280px] sm:w-[320px] bg-enf-surface-deep border border-enf-surface-border rounded-xl overflow-hidden hover:border-enf-border-special/50 transition-colors"
                      >
                        <div className="aspect-[16/9] relative">
                          <img src={selected.image} alt="" className="absolute inset-0 w-full h-full object-cover opacity-70" />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                          <div className="absolute top-2 left-2 px-2 py-0.5 rounded bg-black/70 border border-enf-surface-border text-[10px] uppercase tracking-widest text-enf-role-info font-bold flex items-center gap-1">
                            <Ico className="w-3 h-3" /> {h.meta?.mode || 'session'}
                          </div>
                        </div>
                        <div className="p-4">
                          <div className="font-display text-xl tracking-wide leading-none mb-1">{h.title}</div>
                          <div className="text-xs text-enf-text-muted mb-2">{dt.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}</div>
                          <div className="flex flex-wrap gap-3 text-sm">
                            <span><span className="font-bold">{h.duration}</span><span className="text-enf-text-muted text-xs ml-1">min</span></span>
                            {h.distance && (
                              <span><span className="font-bold">{displayDistance(h.distance, profile.units)}</span><span className="text-enf-text-muted text-xs ml-1">{distanceUnit(profile.units)}</span></span>
                            )}
                            {h.meta?.platform && h.meta.platform !== 'manual' && (
                              <span className="text-xs text-enf-text-muted capitalize ml-auto">{h.meta.platform}</span>
                            )}
                          </div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>
              </WidgetGate>
            )}
            {sessionHistory.length === 0 && (
              <WidgetGate page="recovery" id="recentSessions">
                <div className="p-6 text-center bg-enf-surface-deep border border-enf-surface-border rounded-xl text-sm text-enf-text-muted">
                  No {selected.name.toLowerCase()} sessions logged yet. Tap <span className="text-enf-accent-fg font-semibold">Log session</span> above to start tracking — or wait for cloud device sync (coming soon).
                </div>
              </WidgetGate>
            )}

            {/* Form / Safety / Gear */}
            <div className="grid md:grid-cols-3 gap-3">
              <div className="bg-enf-surface-deep border border-enf-surface-border rounded-xl p-5">
                <div className="flex items-center gap-2 mb-3"><Icons.Sparkles className="w-4 h-4 text-enf-accent-fg" /><div className="text-xs uppercase tracking-widest text-enf-text-secondary font-semibold">Form</div></div>
                <p className="text-sm text-enf-text-secondary leading-relaxed">{selected.form}</p>
              </div>
              <div className="bg-enf-surface-deep border border-enf-surface-border rounded-xl p-5">
                <div className="flex items-center gap-2 mb-3"><ShieldAlert className="w-4 h-4 text-enf-accent-fg" /><div className="text-xs uppercase tracking-widest text-enf-text-secondary font-semibold">Safety</div></div>
                <ul className="space-y-1.5">
                  {selected.safety.map((s, i) => <li key={i} className="text-sm text-enf-text-secondary flex gap-2"><span className="text-enf-accent-fg mt-1">•</span>{s}</li>)}
                </ul>
              </div>
              <div className="bg-enf-surface-deep border border-enf-surface-border rounded-xl p-5">
                <div className="flex items-center gap-2 mb-3"><Wrench className="w-4 h-4 text-enf-accent-fg" /><div className="text-xs uppercase tracking-widest text-enf-text-secondary font-semibold">Gear</div></div>
                <ul className="space-y-1.5">
                  {selected.gear.map((g, i) => <li key={i} className="text-sm text-enf-text-secondary flex gap-2"><span className="text-enf-text-muted mt-1">·</span>{g}</li>)}
                </ul>
              </div>
            </div>

            {/* Affiliate gear recommendations — keyed off the active activity */}
            {(() => {
              const map = {
                'walk-run': 'walk-run',
                'cycling': mode === 'indoor' ? 'cycling-indoor' : 'cycling-outdoor',
                'hiking': 'hiking',
                'rock-climbing': 'rock-climbing',
                'rowing': 'rowing',
                'heavy-bag': 'heavy-bag',
                'elliptical-max': 'full-body-cardio',
                'jump-rope': 'jump-rope',
                'swimming': 'swimming',
              };
              const key = map[selected.id];
              return key ? (
                <WidgetGate page="recovery" id="affiliateGear">
                  <GearRecommendations groupKey={key} />
                </WidgetGate>
              ) : null;
            })()}

            <button onClick={() => navigate('/progress')} className="inline-flex items-center gap-2 text-sm text-enf-text-secondary hover:text-enf-text-primary transition-colors">
              <ArrowLeft className="w-4 h-4" /> Back to dashboard
            </button>
          </div>
        </div>
      </div>

      {/* GPS tracker takeover */}
      {gpsOpen && (
        <GpsTracker
          activity={selected}
          units={profile.units || 'imperial'}
          onComplete={handleGpsComplete}
          onCancel={() => setGpsOpen(false)}
        />
      )}

      <PaywallModal open={gpsPaywallOpen} onOpenChange={setGpsPaywallOpen} feature="gps" />

      {/* AI endurance session generator — interval-based surfaces only */}
      <AIEnduranceModal
        open={aiOpen}
        onClose={() => setAiOpen(false)}
        activity={selected.name}
        mode={hasModes ? mode : null}
        surface={activeSurface?.name || null}
        minutes={recommended?.duration || 30}
        units={profile.units || 'imperial'}
        onApply={handleApplyAiSession}
      />

      <PaywallModal open={aiPaywallOpen} onOpenChange={setAiPaywallOpen} feature="ai_workouts" />

      {/* Manual log dialog (simple inline modal) */}
      {logOpen && (() => {
        const todayISO = localISO(new Date());
        const isHistorical = customLogDate && customLogDate !== todayISO;
        const minDate = (() => { const d = new Date(); d.setDate(d.getDate() - 14); return localISO(d); })();
        let daysAgoLabel = '';
        if (isHistorical) {
          const [y, m, d] = customLogDate.split('-').map(Number);
          const picked = new Date(y, m - 1, d);
          const today = new Date(); today.setHours(0, 0, 0, 0);
          const diff = Math.floor((today.getTime() - picked.getTime()) / 86400000);
          daysAgoLabel = diff === 1 ? 'yesterday' : `${diff} days ago`;
        }
        return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80" onClick={() => setLogOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md bg-enf-surface-deep border border-enf-surface-border rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="font-display text-2xl tracking-wide leading-none">
                {isHistorical ? `Log past ${selected.name.toLowerCase()} session` : `Log ${selected.name} session`}
              </div>
              <button onClick={() => setLogOpen(false)} className="text-enf-text-muted hover:text-enf-text-primary"><Icons.X className="w-5 h-5" /></button>
            </div>
            {isHistorical && (
              <div className="mb-3 text-xs text-enf-role-info flex items-center gap-2">
                <Icons.History className="w-3.5 h-3.5" />
                Logging {daysAgoLabel} — recorded on the actual date.
              </div>
            )}
            <div className="space-y-3">
              <div>
                <label className="text-xs uppercase tracking-widest text-enf-text-muted font-semibold mb-2 block">Date</label>
                <input
                  type="date"
                  min={minDate}
                  max={todayISO}
                  value={customLogDate}
                  onChange={(e) => setCustomLogDate(e.target.value || todayISO)}
                  className="w-full px-3 py-2 bg-enf-surface-mid border border-enf-surface-border rounded-md text-enf-text-primary focus:outline-none focus:border-enf-accent-fg"
                  data-testid="endurance-log-date-input"
                />
                <p className="text-[11px] text-enf-text-muted mt-1">Defaults to today. Pick any date in the last 14 days for backfill.</p>
              </div>

              {/* Mode + Surface pickers — only render when activity has modes. */}
              {hasModes && (
                <>
                  <div>
                    <label className="text-xs uppercase tracking-widest text-enf-text-muted font-semibold mb-2 block">Mode</label>
                    <div className="grid grid-cols-2 gap-2">
                      {Object.entries(selected.modes).map(([mId, mObj]) => (
                        <button
                          key={mId}
                          onClick={() => { setLogMode(mId); setLogSurfaceId(null); }}
                          className={`px-2 py-1.5 text-xs rounded-md border capitalize transition-colors ${
                            effectiveLogMode === mId ? 'bg-enf-accent border-enf-accent-fg text-enf-text-primary' : 'bg-enf-surface-mid border-enf-surface-border text-enf-text-secondary hover:text-enf-text-primary'
                          }`}
                          data-testid={`log-mode-${mId}`}
                        >
                          {mObj.label || mId}
                        </button>
                      ))}
                    </div>
                  </div>
                  {effectiveLogSurfaces.length > 0 && (
                    <div>
                      <label className="text-xs uppercase tracking-widest text-enf-text-muted font-semibold mb-2 block">Surface / location</label>
                      <div className="grid grid-cols-2 gap-2">
                        {effectiveLogSurfaces.map((s) => (
                          <button
                            key={s.id}
                            onClick={() => setLogSurfaceId(s.id)}
                            className={`px-2 py-1.5 text-xs rounded-md border transition-colors ${
                              effectiveLogSurface?.id === s.id ? 'bg-enf-role-info/20 border-enf-role-info text-enf-role-info' : 'bg-enf-surface-mid border-enf-surface-border text-enf-text-secondary hover:text-enf-text-primary'
                            }`}
                            data-testid={`log-surface-${s.id}`}
                          >
                            {s.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
              <div>
                <label className="text-xs uppercase tracking-widest text-enf-text-muted font-semibold mb-2 block">Platform / source</label>
                <div className="grid grid-cols-3 gap-2">
                  {['manual', 'peloton', 'zwift', 'wahoo', 'trainerroad', 'other'].map((p) => (
                    <button key={p} onClick={() => setLogForm((f) => ({ ...f, platform: p }))}
                      className={`px-2 py-1.5 text-xs rounded-md border capitalize transition-colors ${
                        logForm.platform === p ? 'bg-enf-accent border-enf-accent-fg text-enf-text-primary' : 'bg-enf-surface-mid border-enf-surface-border text-enf-text-secondary hover:text-enf-text-primary'
                      }`}>{p}</button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs uppercase tracking-widest text-enf-text-muted font-semibold mb-2 block">Duration (min)</label>
                  <input type="number" value={logForm.duration} onChange={(e) => setLogForm((f) => ({ ...f, duration: e.target.value }))}
                    placeholder="45" className="w-full px-3 py-2 bg-enf-surface-mid border border-enf-surface-border rounded-md text-enf-text-primary focus:outline-none focus:border-enf-accent-fg" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-enf-text-muted font-semibold mb-2 block">Distance ({distanceUnit(profile.units)})</label>
                  <input type="number" step="0.1" value={logForm.distance} onChange={(e) => setLogForm((f) => ({ ...f, distance: e.target.value }))}
                    placeholder={profile.units === 'metric' ? '20.0' : '12.4'} className="w-full px-3 py-2 bg-enf-surface-mid border border-enf-surface-border rounded-md text-enf-text-primary focus:outline-none focus:border-enf-accent-fg" />
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-enf-text-muted font-semibold mb-2 block">Notes</label>
                <textarea value={logForm.notes} onChange={(e) => setLogForm((f) => ({ ...f, notes: e.target.value }))}
                  placeholder="Class name, instructor, how it felt..." className="w-full px-3 py-2 bg-enf-surface-mid border border-enf-surface-border rounded-md text-enf-text-primary focus:outline-none focus:border-enf-accent-fg min-h-[70px] resize-none" />
              </div>
            </div>
            <button onClick={handleLogSession} className="w-full mt-5 py-3 bg-enf-accent hover:bg-enf-accent-hover rounded-md font-semibold text-sm flex items-center justify-center gap-2 transition-colors" data-testid="endurance-log-submit">
              <Trophy className="w-4 h-4" /> {isHistorical ? `Log for ${(() => { const [y, m, d] = customLogDate.split('-').map(Number); return new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }); })()}` : 'Log session'}
            </button>
          </div>
        </div>
        );
      })()}
    </div>
  );
};

export default Endurance;
